// frontend/src/components/announcements/AnnouncementList.jsx
// List component for viewing and managing announcements

import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const AnnouncementList = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filter, setFilter] = useState({
    includeExpired: true,
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        includeExpired: filter.includeExpired,
      });

      const response = await secureAxios.get(`/api/announcements?${params}`);
      setAnnouncements(response.data.announcements || []);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      console.error("Error fetching announcements:", err);
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filter.includeExpired]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await secureAxios.delete(`/api/announcements/${id}`);
      toast.success("Announcement deleted successfully");
      setDeleteConfirm(null);
      fetchAnnouncements();
    } catch (err) {
      console.error("Error deleting announcement:", err);
      toast.error("Failed to delete announcement");
    }
  };

  // Toggle active status
  const handleToggleActive = async (announcement) => {
    try {
      await secureAxios.put(
        `/api/announcements/${announcement.announcement_id}`,
        {
          is_active: !announcement.is_active,
        }
      );
      toast.success(
        `Announcement ${
          announcement.is_active ? "deactivated" : "activated"
        } successfully`
      );
      fetchAnnouncements();
    } catch (err) {
      console.error("Error toggling announcement status:", err);
      toast.error("Failed to update announcement status");
    }
  };

  // Get status information
  const getStatus = (announcement) => {
    const now = new Date();
    const startDate = new Date(announcement.start_date);
    const endDate = announcement.end_date
      ? new Date(announcement.end_date)
      : null;

    if (!announcement.is_active) {
      return {
        label: "Inactive",
        class: "bg-secondary",
        icon: "bi-pause-circle",
      };
    }
    if (startDate > now) {
      return {
        label: "Scheduled",
        class: "bg-info",
        icon: "bi-calendar-event",
      };
    }
    if (endDate && endDate < now) {
      return {
        label: "Expired",
        class: "bg-warning text-dark",
        icon: "bi-clock-history",
      };
    }
    return { label: "Active", class: "bg-success", icon: "bi-broadcast" };
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const badges = {
      urgent: { class: "bg-danger", label: "Urgent" },
      high: { class: "bg-warning text-dark", label: "High" },
      normal: { class: "bg-info", label: "Normal" },
      low: { class: "bg-secondary", label: "Low" },
    };
    return badges[priority] || badges.normal;
  };

  // Get priority border color
  const getPriorityBorderColor = (priority) => {
    const colors = {
      urgent: "#dc3545",
      high: "#ffc107",
      normal: "#0dcaf0",
      low: "#6c757d",
    };
    return colors[priority] || colors.normal;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "No expiration";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="announcements-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2>
            <i className="bi bi-megaphone me-2"></i>
            System Announcements
          </h2>
          <p className="text-muted mb-0">
            Manage announcements visible to all tenants
          </p>
        </div>
        <Link to="create" className="btn btn-primary text-nowrap">
          <i className="bi bi-plus-lg me-1"></i>
          New Announcement
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body py-2">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="includeExpired"
              checked={filter.includeExpired}
              onChange={(e) => {
                setFilter({ ...filter, includeExpired: e.target.checked });
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
            />
            <label className="form-check-label" htmlFor="includeExpired">
              Include expired announcements
            </label>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading announcements...</p>
        </div>
      ) : announcements.length === 0 ? (
        /* Empty State */
        <div className="text-center py-5">
          <i className="bi bi-megaphone display-1 text-muted"></i>
          <h4 className="mt-3 text-muted">No Announcements</h4>
          <p className="text-muted">
            Create your first announcement to get started.
          </p>
          <Link to="create" className="btn btn-primary">
            <i className="bi bi-plus-lg me-1"></i>
            Create Announcement
          </Link>
        </div>
      ) : (
        /* Announcements List */
        <div>
          {announcements.map((announcement) => {
            const status = getStatus(announcement);
            const priority = getPriorityBadge(announcement.priority);

            return (
              <div
                key={announcement.announcement_id}
                className={`card mb-3 ${
                  !announcement.is_active ? "opacity-50" : ""
                } ${status.label === "Expired" ? "opacity-75" : ""}`}
                style={{
                  borderLeft: `4px solid ${getPriorityBorderColor(
                    announcement.priority
                  )}`,
                }}
              >
                <div className="card-header d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h5 className="mb-1">{announcement.title}</h5>
                    <div className="d-flex gap-2 flex-wrap">
                      <span className={`badge ${status.class}`}>
                        <i className={`bi ${status.icon} me-1`}></i>
                        {status.label}
                      </span>
                      <span className={`badge ${priority.class}`}>
                        {priority.label}
                      </span>
                    </div>
                  </div>
                  <div className="btn-group flex-shrink-0">
                    <Link
                      to={`edit/${announcement.announcement_id}`}
                      className="btn btn-outline-primary btn-sm"
                    >
                      <i className="bi bi-pencil"></i>
                    </Link>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => handleToggleActive(announcement)}
                      title={announcement.is_active ? "Deactivate" : "Activate"}
                    >
                      <i
                        className={`bi ${
                          announcement.is_active ? "bi-pause" : "bi-play"
                        }`}
                      ></i>
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() =>
                        setDeleteConfirm(announcement.announcement_id)
                      }
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <p className="mb-0">
                    {announcement.content.length > 200
                      ? `${announcement.content.substring(0, 200)}...`
                      : announcement.content}
                  </p>
                </div>

                <div className="card-footer text-muted small d-flex justify-content-between flex-wrap gap-2">
                  <div>
                    <span className="me-3">
                      <i className="bi bi-calendar-plus me-1"></i>
                      Start: {formatDate(announcement.start_date)}
                    </span>
                    <span>
                      <i className="bi bi-calendar-x me-1"></i>
                      End: {formatDate(announcement.end_date)}
                    </span>
                  </div>
                  <div>
                    <span className="badge bg-light text-dark me-2">
                      <i className="bi bi-eye me-1"></i>
                      {announcement.viewed_count || 0} viewed
                    </span>
                    <span className="badge bg-light text-dark">
                      <i className="bi bi-check-circle me-1"></i>
                      {announcement.dismissed_count || 0} dismissed
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center flex-wrap">
                <li
                  className={`page-item ${
                    pagination.page === 1 ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                </li>
                {[...Array(pagination.totalPages)].map((_, idx) => (
                  <li
                    key={idx}
                    className={`page-item ${
                      pagination.page === idx + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        setPagination((prev) => ({ ...prev, page: idx + 1 }))
                      }
                    >
                      {idx + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    pagination.page === pagination.totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                  Confirm Delete
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteConfirm(null)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this announcement?</p>
                <p className="text-muted small mb-0">
                  This action cannot be undone. All user interaction history for
                  this announcement will also be deleted.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleDelete(deleteConfirm)}
                >
                  <i className="bi bi-trash me-1"></i>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementList;
